import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Description as DocumentIcon,
  EventAvailable as LeaveIcon,
  Timeline as TimelineIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Import tab components
import OverviewTab from "./employee/OverviewTab";
import PaystubsTab from "./employee/PaystubsTab";
import ImmigrationTab from "./employee/ImmigrationTab";
import LeaveRequestTab from "./employee/LeaveRequestTab";
import MilestonesTab from "./employee/MilestonesTab";
import TimesheetTab from "./employee/TimesheetTab";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `employee-tab-${index}`,
    "aria-controls": `employee-tabpanel-${index}`,
  };
}

const EmployeeDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const tabs = [
    { label: "Overview", icon: <DashboardIcon />, component: <OverviewTab /> },
    { label: "Paystubs", icon: <ReceiptIcon />, component: <PaystubsTab /> },
    { label: "Immigration", icon: <DocumentIcon />, component: <ImmigrationTab /> },
    { label: "Leave Request", icon: <LeaveIcon />, component: <LeaveRequestTab /> },
    { label: "Milestones", icon: <TimelineIcon />, component: <MilestonesTab /> },
    { label: "Timesheet", icon: <ScheduleIcon />, component: <TimesheetTab /> },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
            borderRadius: theme.spacing(2),
            boxShadow: theme.shadows[10],
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Employee Portal
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Welcome back, {user?.firstName} {user?.lastName}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your employment information, track progress, and stay organized.
          </Typography>
        </Paper>

        {/* Tabs */}
        <Paper
          sx={{
            background: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
            borderRadius: theme.spacing(2),
            boxShadow: theme.shadows[10],
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="employee portal tabs"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  minHeight: 72,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box sx={{ p: 3 }}>
            {tabs.map((tab, index) => (
              <TabPanel key={index} value={activeTab} index={index}>
                {tab.component}
              </TabPanel>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EmployeeDashboard;
